import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSignal } from '@preact/signals-react'
import { Autocomplete, Box, Button, Chip, IconButton, LinearProgress, Link, TextField, Tooltip, Typography } from '@mui/material'
import {
	AppCrudTable, AppDialog, AppDialogHeader, AppDialogBody, AppDialogFooter, AppDialogCancel,
	AppNote, AppEmptyLine, AppTableSkeleton, secondaryButtonSx,
} from '@components'
import { dsColorNew } from '@palette'
import AppTableSearch from '@components/@base/Datatable/Search'
import { displayDay } from '@utils'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'
import { ManageSearchOutlined as OpenSearchIcon } from 'Pages/DsRepricing/@components/icons'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { realTimeSearchHref } from 'Pages/DsRepricing/@services/realTimeHref'
import { executionsApi } from 'Pages/DsRepricing/Executions/@services/api'
import { PUSH_STYLE } from 'Pages/DsRepricing/Executions/@services/pushStyle'
import { passCounts } from 'Pages/DsRepricing/Executions/@services/passCounts'
import { OUTCOME_STYLE } from 'Pages/DsRepricing/Executions/@components/ChangesDialog'
import { canonicalEventType } from 'Pages/DsRepricing/Setup/@mocks/activityLog'
import {
	lengthsLabel, sitesPreview, gapNote, rowProgress, searchText, officeLabel,
	eventKinds, kindOptions, filterByKinds, countLabel,
} from 'Pages/DsRepricing/Executions/@services/passSearches'

const UNIT = 'a search is one office and one pick-up date, with all its sites and rental lengths'

//: The pass row's Decisions vocabulary (`OUTCOME_STYLE`) plus `outranked`, which
//: is not an event type: a decision another strategy beat (criterion 12, UX Obs. 16).
const ENGINE_STYLE = { ...OUTCOME_STYLE, outranked: { tone: 'neutral', label: 'outranked' } }
const OUTRANKED_TITLE = 'Decided, never sent: another strategy outranked it on this bracket, or won the tie by moving the price more.'
const styleOf = (kind) => ENGINE_STYLE[kind] || { tone: 'neutral', label: kind.replace(/_/g, ' ') }
const cellSx = { px: 1.5, py: 1, fontSize: 13 }
const dash = (title) => (
	<Box component="span" title={title} sx={{ color: dsColorNew.textSubtle }}>—</Box>
)

/**
 * The searches of one pass (CU-869fmm943, UX Obs. 2-6): what is behind the
 * "N searches" of its row on Executions. One row per search the pass
 * launched — one office and one pick-up date, every site and rental length
 * inside — with its PMS push and a real link that opens it in Real time
 * Searches in a new tab, so this dialog stays open.
 *
 * `AppCrudTable` (K3): search, sort, Excel and pagination come with it.
 * The whole list arrives in one answer, so nothing here round-trips.
 */
export default function PassSearchesDialog({ run, title, onClose }) {
	const [state, setState] = useState({ loading: true, body: null, error: null })

	const load = useCallback(async () => {
		setState({ loading: true, body: null, error: null })
		try {
			setState({ loading: false, body: await executionsApi.searches(run.execution_id), error: null })
		} catch (e) {
			setState({ loading: false, body: null, error: e?.message || 'unknown error' })
		}
	}, [run.execution_id])
	useEffect(() => { load() }, [load])

	const counts = passCounts(run)
	const items = state.body?.items || []
	const gap = state.body ? gapNote(state.body.composed, state.body.listed) : null
	const rows = useMemo(() => toRows(items), [items])
	// Criterion 12: what the engine did with each search. Client-side, not in the URL,
	// gone when the dialog closes (UX Obs. 15).
	const eventsOk = state.body ? state.body.events_ok !== false : true
	const [kinds, setKinds] = useState([])
	// A signal, not state: mui-datatables keeps the empty-line element it got first, so the text must
	// reach NoMatch without a new element (seen in the browser: it always said "no search text").
	const query = useSignal('')
	const setQuery = (text) => { query.value = text }
	const [tableKey, setTableKey] = useState(0)
	const options = useMemo(() => kindOptions(items, canonicalEventType), [items])
	const shown = useMemo(() => filterByKinds(rows, kinds, canonicalEventType), [rows, kinds])
	const clearBoth = () => { setKinds([]); query.value = ''; setTableKey(k => k + 1) }

	return (
		<AppDialog open onClose={onClose} maxWidth="lg" paperSx={setupScopeSx}>
			<AppDialogHeader icon="magnifying-glass" onClose={onClose}
			                 title={`Searches · ${title}`}
			                 subtitle={state.loading ? '' : `${counts.label} · ${UNIT}`}/>
			<AppDialogBody sx={{ ...dialogBodySx, p: 2, borderTop: `1px solid ${dsColorNew.grayBorder}` }}>
				{state.loading && (
					<>
						<OutcomeFilter options={[]} value={[]} onChange={() => {}} loading shown={0} total={0}/>
						<AppTableSkeleton rows={5} columns={8}/>
					</>
				)}
				{state.error && (
					<AppNote severity="error">
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
							<span>Could not load the searches of this pass: {state.error}.</span>
							<Button size="small" variant="outlined" onClick={load} sx={{ ...secondaryButtonSx, py: 0.25 }}>Retry</Button>
						</Box>
					</AppNote>
				)}
				{state.body && items.length === 0 && (
					<AppEmptyLine>
						No searches on record for this pass. Older passes did not keep the list; the counts on its row are all there is.
					</AppEmptyLine>
				)}
				{gap && <AppNote sx={{ mb: 1.5 }}>{gap}</AppNote>}
				{items.length > 0 && (
					<>
						<SearchesTable key={tableKey} rows={shown} eventsOk={eventsOk} onSearch={setQuery}
						               title={<OutcomeFilter options={options} value={kinds} onChange={setKinds} disabled={!eventsOk}
						                                     shown={shown.length} total={rows.length}/>}
						               noMatch={kinds.length > 0 ? (
							               <NoMatch query={query} onClearOutcomes={() => setKinds([])} onClearBoth={clearBoth}/>
						               ) : undefined}/>
					</>
				)}
			</AppDialogBody>
			<AppDialogFooter>
				<AppDialogCancel onClick={onClose}>Close</AppDialogCancel>
			</AppDialogFooter>
		</AppDialog>
	)
}

/** Flat rows for the table: the sortable values next to the item they come from. */
function toRows(items) {
	return [...items]
		.map(item => {
			const office = officeLabel(item.office)
			const progress = rowProgress(item)
			return {
				...item,
				officeName: office.name,
				pickup: item.pickup_date || '',
				progressPct: progress ? progress.pct : -1,
				pushLabel: PUSH_STYLE[item.push?.state]?.label || '',
				searchText: searchText(item),
			}
		})
		.sort((a, b) => a.officeName.localeCompare(b.officeName) || a.pickup.localeCompare(b.pickup))
}

/** "Engine outcome": Activity's Event field, on the kinds this pass has (UX Obs. 15, 18). */
function OutcomeFilter({ options, value, onChange, disabled, loading, shown, total }) {
	const countOf = Object.fromEntries(options.map(o => [o.kind, o.n]))
	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 0.5 }}>
			<Autocomplete multiple size="small" sx={{ width: 340 }}
			              disabled={disabled || loading}
			              options={options.map(o => o.kind)}
			              value={value}
			              onChange={(_, v) => onChange(v)}
			              getOptionLabel={(k) => `${styleOf(k).label} ${countOf[k] ?? ''}`.trim()}
			              renderTags={(list, getTagProps) => list.map((k, idx) => (
				              <Chip {...getTagProps({ index: idx })} key={k} size="small" label={styleOf(k).label} sx={toneChipSx(styleOf(k).tone)}/>
			              ))}
			              renderOption={(props, k) => (
				              <li {...props} key={k}>
					              <Chip size="small" label={`${styleOf(k).label} ${countOf[k] ?? ''}`} sx={toneChipSx(styleOf(k).tone)}
					                    title={k === 'outranked' ? OUTRANKED_TITLE : undefined}/>
				              </li>
			              )}
			              renderInput={(params) => (
				              <TextField {...params} label="Engine outcome" InputLabelProps={{ shrink: true }}
				                         placeholder={disabled ? 'Engine outcomes unavailable' : (value.length ? '' : 'Any outcome')}/>
			              )}/>
			{disabled && (
				<Typography sx={{ fontSize: 13, color: dsColorNew.noteAmber }}>
					Could not load what the engine did; the list is complete.
				</Typography>
			)}
			{!disabled && value.length > 0 && (
				<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>{countLabel(shown, total)}</Typography>
			)}
		</Box>
	)
}

function NoMatch({ query: querySignal, onClearOutcomes, onClearBoth }) {
	const query = querySignal.value
	const linkSx = { fontSize: 'inherit', color: dsColorNew.appBlue, verticalAlign: 'baseline' }
	return (
		<AppEmptyLine>
			{query
				? <>No search of this pass matches “{query}” with these engine outcomes.</>
				: 'No search of this pass has these engine outcomes.'}{' '}
			<Link component="button" type="button" onClick={onClearOutcomes} sx={linkSx}>Clear the outcomes</Link>
			{query && <>{' · '}<Link component="button" type="button" onClick={onClearBoth} sx={linkSx}>Clear both</Link></>}
		</AppEmptyLine>
	)
}

function SearchesTable({ rows, eventsOk, onSearch, noMatch, title }) {
	const columns = [
		{ name: 'officeName', label: 'Office', options: { filter: false, customBodyRowRender: (_, row) => <OfficeCell office={row.office}/> } },
		{ name: 'pickup', label: 'Pick-up', options: { filter: false, customBodyRowRender: v => <Box sx={{ whiteSpace: 'nowrap' }}>{displayDay(v)}</Box> } },
		{ name: 'lors', label: 'Rental lengths', options: { filter: false, sort: false, customBodyRowRender: v => lengthsLabel(v) } },
		{ name: 'sites', label: 'Sites', options: { filter: false, sort: false, customBodyRowRender: v => <SitesCell sites={v}/> } },
		{ name: 'progressPct', label: 'Progress', options: { filter: false, customBodyRowRender: (_, row) => <ProgressCell item={row}/> } },
		{ name: 'pushLabel', label: 'PMS push', options: { filter: false, customBodyRowRender: (_, row) => <PushChip push={row.push}/> } },
		{ name: 'events', label: 'Engine', options: { filter: false, sort: false, download: false,
		  setCellHeaderProps: () => ({ title: "What the engine did with this search's prices, by kind" }),
		  customBodyRowRender: v => <EngineCell events={v} eventsOk={eventsOk}/> } },
		{ name: 'id_search', label: 'Open', options: { filter: false, sort: false, download: false,
		  setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
		  customBodyRowRender: (v, row) => <OpenCell idSearch={v} row={row}/> } },
		{ name: 'searchText', label: 'Search', options: { display: 'excluded', filter: false, download: false } },
	]
	const SEARCH_COLUMN = columns.length - 1
	return (
		<Box sx={{ '& td': cellSx, '& th': { px: 1.5 } }}>
			{/* The outcome field in its own row above the table (UX Obs. 15), so the table's own search —
			    which mui-datatables opens on the left of its toolbar — never sits under it (QA 2026-10-27).
			    Not in `title`: mui-datatables repeats the title in the caption and would mount it twice. */}
			{title && <Box sx={{ mb: 0.5 }}>{title}</Box>}
			<AppCrudTable
				// AppDataTable reads the empty line from the `textLabels` prop, not from `options`
				textLabels={noMatch ? { body: { noMatch } } : undefined}
				data={rows}
				columns={columns}
				options={{
					rowsPerPage: 20,
					filter: false,
					searchPlaceholder: 'Search an office or a site',
					// The office (name and code) and every site of the row — not only the visible cells.
					customSearch: (query, row) => String(row[SEARCH_COLUMN] || '').toLowerCase()
						.includes(String(query || '').toLowerCase()),
					// The app's search box, as AppDataTable draws it, also telling the dialog what was searched:
					// the empty line names it (UX Obs. 18). mui-datatables' onSearchChange does not fire here.
					customSearchRender: (searchText, search, hide) => (
						<AppTableSearch onSearch={(text) => { search(text); onSearch && onSearch(text || '') }}
						                onHide={() => { hide(); onSearch && onSearch('') }}/>
					),
				}}
			/>
		</Box>
	)
}

function OfficeCell({ office }) {
	const o = officeLabel(office)
	return (
		<Box sx={{ whiteSpace: 'nowrap' }}>
			<Box component="span" sx={{ fontWeight: 700 }}>{o.name}</Box>
			{o.code && <Box component="span" sx={{ ml: 0.75, color: dsColorNew.textMuted }}>{o.code}</Box>}
		</Box>
	)
}

function SitesCell({ sites }) {
	const p = sitesPreview(sites)
	if (!p || !p.first) return dash('The sites of this search are no longer on record')
	return (
		<Tooltip title={p.all}>
			<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
				<span>{p.first}</span>
				{p.more > 0 && <Chip size="small" label={`+${p.more}`} sx={toneChipSx('neutral')}/>}
			</Box>
		</Tooltip>
	)
}

function ProgressCell({ item }) {
	const p = rowProgress(item)
	if (!p) return dash('The progress of this search is no longer on record')
	const failed = item.progress?.error > 0
	return (
		<Box sx={{ minWidth: 150 }}>
			<LinearProgress variant="determinate" value={p.pct}
			                sx={{ width: 96, height: 6, borderRadius: 3, bgcolor: dsColorNew.pillTrackBg,
			                      '& .MuiLinearProgress-bar': { bgcolor: failed ? dsColorNew.dangerRed : dsColorNew.successText } }}/>
			<Typography sx={{ fontSize: 12, color: failed ? dsColorNew.dangerRed : dsColorNew.textMuted, mt: 0.5, whiteSpace: 'nowrap' }}>
				{p.label}
			</Typography>
		</Box>
	)
}

/** The pass row's Decisions chips for one search: largest first, at most two, then +N (UX Obs. 16). */
function EngineCell({ events, eventsOk }) {
	if (!eventsOk || events == null) return dash('Engine outcomes could not be loaded')
	const list = eventKinds(events, canonicalEventType)
	if (!list.length) return dash('The engine has not decided anything for this search')
	const chip = ([k, n]) => (
		<Chip key={k} size="small" label={`${styleOf(k).label} ${n}`} sx={toneChipSx(styleOf(k).tone)}
		      title={k === 'outranked' ? OUTRANKED_TITLE : undefined}/>
	)
	const rest = list.slice(2)
	return (
		<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
			{list.slice(0, 2).map(chip)}
			{rest.length > 0 && (
				<Tooltip title={list.map(([k, n]) => `${styleOf(k).label} ${n}`).join(' · ')}>
					<Chip size="small" label={`+${rest.length}`} sx={toneChipSx('neutral')}/>
				</Tooltip>
			)}
		</Box>
	)
}

function PushChip({ push }) {
	const s = PUSH_STYLE[push?.state]
	if (!s) return dash('Nothing decided to send for this search')
	return <Chip size="small" label={s.label} sx={toneChipSx(s.tone)}/>
}

function OpenCell({ idSearch, row }) {
	const o = officeLabel(row.office)
	return (
		<Box sx={{ textAlign: 'center' }}>
			<IconButton component="a" href={realTimeSearchHref(idSearch)} target="_blank" rel="noopener noreferrer"
			            title="View in Real Time Searches" size="small"
			            aria-label={`View in Real Time Searches: ${o.name}, ${displayDay(row.pickup_date)}`}
			            sx={{ color: dsColorNew.appBlue, p: 0.25, '&:hover': { bgcolor: dsColorNew.pillActiveBg } }}>
				<OpenSearchIcon sx={{ fontSize: 18 }}/>
			</IconButton>
		</Box>
	)
}
